// Von BdmTable und Part2Table geteilte Tabellen-Bausteine:
// TitledTable (Shell), StatusCell und ActionButton.
import { ReactNode } from 'react';
import { Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { RetrievalStatus } from '@prisma/client';
import type { RetrievalResDto } from '@web/common';
import {
  ACTIONS,
  STATUS,
  actionFor,
  fmtDate,
  type ActionKind,
} from './BdmRetrievalsSection.helpers';

// Betitelte Tabelle: Überschrift + gestylte Header-Zeile aus `columns`, `children` = Rows.
export const TitledTable = ({
  title,
  columns,
  children,
  action,
}: {
  title: string;
  columns: string[];
  children: ReactNode;
  // shown on the right of the title
  action?: ReactNode;
}) => (
  <div>
    <Group justify="space-between" align="center" mb="8px">
      <Text fw="600" size="16px">
        {title}
      </Text>
      {action}
    </Group>
    <Table
      verticalSpacing="sm"
      stickyHeader
      highlightOnHover
      highlightOnHoverColor="#f6f5ef"
    >
      <Table.Thead bg="#f6f5ef" fw="500" lh="24px">
        <Table.Tr>
          {columns.map((c) => (
            <Table.Td key={c}>{c}</Table.Td>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{children}</Table.Tbody>
    </Table>
  </div>
);

// Detailzeile unter dem Status-Badge: Videocall-Zeit bzw. Versand-/Rückläufer-/Abhol-Datum.
const statusDetail = (r: RetrievalResDto, t: TFunction): string | null => {
  if (r.status === RetrievalStatus.BOOKED && r.Appointment) {
    return t('Panel.StatusSub.Videocall', {
      time: DateTime.fromJSDate(r.Appointment.startTime).toFormat(
        'dd.MM.yyyy, HH:mm',
      ),
    });
  }
  if (r.status === RetrievalStatus.DISPATCHED && r.dispatchedAt) {
    return t('Panel.StatusSub.Dispatched', { date: fmtDate(r.dispatchedAt) });
  }
  if (r.status === RetrievalStatus.RETURNED_READY_FOR_PICKUP && r.returnedAt) {
    return t('Panel.StatusSub.Returned', { date: fmtDate(r.returnedAt) });
  }
  if (r.status === RetrievalStatus.PICKED_UP && r.pickedUpAt) {
    return t('Panel.StatusSub.PickedUp', { date: fmtDate(r.pickedUpAt) });
  }
  return null;
};

// Status-Badge + Detailzeile. Kein Retrieval → Strich; Voucher → dezenter Text ohne Badge.
export const StatusCell = ({ retrieval }: { retrieval?: RetrievalResDto }) => {
  const { t } = useTranslation('nachversand');

  if (!retrieval) {
    return (
      <Text size="12px" c="dimmed">
        –
      </Text>
    );
  }
  if (retrieval.status === RetrievalStatus.NO_FULFILLMENT) {
    return (
      <Text size="13px" c="dimmed">
        {t('Panel.Result.NoPhysical')}
      </Text>
    );
  }

  const meta = STATUS[retrieval.status];
  const detail = statusDetail(retrieval, t);
  return (
    <Stack gap="6px">
      <Badge color={meta.color}>{t(meta.labelKey)}</Badge>
      {detail && (
        <Text size="12px" c="dimmed">
          {detail}
        </Text>
      )}
    </Stack>
  );
};

// Staff-Aktion für den aktuellen Status; kein erlaubter Übergang → Strich.
export const ActionButton = ({
  retrieval,
  onAction,
}: {
  retrieval?: RetrievalResDto;
  onAction: (retrieval: RetrievalResDto, kind: ActionKind) => void;
}) => {
  const { t } = useTranslation('nachversand');
  const kind = retrieval ? actionFor(retrieval.status) : null;
  if (!retrieval || !kind) {
    return (
      <Text size="12px" c="dimmed">
        –
      </Text>
    );
  }
  return (
    <Button
      size="xs"
      variant={kind === 'return' ? 'outline' : 'filled'}
      onClick={() => onAction(retrieval, kind)}
    >
      {t(ACTIONS[kind].labelKey)}
    </Button>
  );
};
